/**
 * 씨마켓 임베드(iframe) ↔ 세모 페이지 공용 메시징 헬퍼.
 *
 * 세모 화면은 씨마켓 안에 iframe 으로 떠 있을 때가 있다(견적 참여·견적서·채팅·물품관리).
 * 그 안에서 `window.close()` 나 `window.open()` 은 각각 무동작·팝업차단이 되므로,
 * 부모에게 무엇을 해달라고 알리는 경로가 필요하다.
 *
 * ## Protocol
 * - `SEMO_EMBED_CLOSE` — 부모 모달에게 닫으라는 신호. `reason` 으로 후속 처리(refetch 등)를 가른다.
 * - `SEMO_EMBED_NAVIGATE` — 부모에게 **자기 화면**으로 데려가 달라는 신호. URL 이 아니라
 *   목적지 이름(`target`)을 보낸다 — 씨마켓 라우트를 세모가 하드코딩하면 환경별 도메인·
 *   경로 변경이 세모 배포를 끌고 다니게 된다. 경로 해석은 부모가 소유한다.
 * - `SEMO_EMBED_OPEN_URL` — 부모에게 **외부 URL** 을 새 창으로 열어 달라는 신호(카드 결제창).
 *   iframe 안에서 `window.open` 을 직접 부르면, 특히 mutation 콜백처럼 사용자 제스처와 끊긴
 *   자리에서 호출되면 Safari 가 사실상 차단한다. 부모의 사용자 컨텍스트를 빌린다.
 * - `SEMO_EMBED_READY` — 부모에게 **세모 화면이 다 그려졌다**고 알리는 신호. 부모는 이걸 받고
 *   자기 스플래시를 걷는다. iframe 의 `load` 이벤트로 걷으면 안 되기 때문이다 — `load` 는
 *   "파일이 다 내려왔다"는 뜻이지 "화면이 준비됐다"는 뜻이 아니다.
 *
 * `targetOrigin` 은 `"*"` 를 쓴다. 씨마켓 스테이징·프로덕션 도메인이 여러 개이고 팝업 흐름과
 * iframe 흐름을 모두 커버해야 하기 때문이며, 페이로드에 민감정보가 없어 유출 위험이 없다.
 * (수신 측은 origin 을 검증한다.)
 */

const EMBED_CLOSE_MESSAGE_TYPE = "SEMO_EMBED_CLOSE" as const;
const EMBED_NAVIGATE_MESSAGE_TYPE = "SEMO_EMBED_NAVIGATE" as const;
const EMBED_OPEN_URL_MESSAGE_TYPE = "SEMO_EMBED_OPEN_URL" as const;
const EMBED_READY_MESSAGE_TYPE = "SEMO_EMBED_READY" as const;

export type EmbedCloseReason = "closed" | "submitted" | "canceled";

/**
 * 부모가 데려가 줄 목적지.
 * - `my-contracts`: 씨마켓 '나의 계약 관리' — 계산서 발급요청이 실제로 일어나는 곳.
 * - `payment-window`: 씨마켓 '나의 거래 관리 > 결제창'. `cardPaymentRequestId` 를 같이 보내면
 *   부모가 그 결제창 한 건을 걸러 결제 모달까지 연다.
 * - `card-payment-signup`: 씨마켓 이노페이 가맹 가입 모달. 카드로 계약이 섰는데 공급사가
 *   미가맹이라 결제창이 안 열릴 때, 채팅 카드의 [이노페이 가입하러 가기] 가 보낸다.
 *   목적지를 씨마켓 페이지로 **이동**시키지 않고 부모가 자기 모달을 그 자리에서 연다 —
 *   공급사가 채팅을 벗어나지 않아야 계약 맥락을 잃지 않는다.
 */
export type EmbedNavigateTarget =
  | "my-contracts"
  | "payment-window"
  | "card-payment-signup";

interface EmbedCloseMessage {
  type: typeof EMBED_CLOSE_MESSAGE_TYPE;
  reason: EmbedCloseReason;
}

interface EmbedNavigateMessage {
  type: typeof EMBED_NAVIGATE_MESSAGE_TYPE;
  target: EmbedNavigateTarget;
  /** 부모가 해당 건을 집어 보여줄 때 쓰는 세모 계약 ID. 없으면 목록만 연다. */
  externalContractId?: string;
  /** 씨마켓 결제창 번호(`CP…`). `payment-window` 에서 결제 모달까지 열 때 쓴다. */
  cardPaymentRequestId?: string;
}

/**
 * 부모(씨마켓 모달 또는 오프너 팝업) 에게 embed 닫힘을 통지.
 * iframe 이 아닌 최상단 창에서 호출되면 window.parent === window 라 self-post 가 되지만 무해.
 */
export function notifyParentEmbedClose(reason: EmbedCloseReason): void {
  if (typeof window === "undefined") return;
  const message: EmbedCloseMessage = {
    type: EMBED_CLOSE_MESSAGE_TYPE,
    reason,
  };
  window.parent?.postMessage(message, "*");
}

/**
 * 부모에게 자기 화면으로 데려가 달라고 요청.
 *
 * 임베드 밖(최상단 창)에서는 부모가 없으므로 아무 일도 하지 않고 `false` 를 돌려준다 —
 * 호출부가 그 경우에 버튼을 아예 안 그리도록 `isEmbeddedInIframe()` 과 함께 쓴다.
 */
export function requestParentNavigate(
  target: EmbedNavigateTarget,
  options?: { externalContractId?: string; cardPaymentRequestId?: string },
): boolean {
  if (typeof window === "undefined" || !isEmbeddedInIframe()) return false;
  const message: EmbedNavigateMessage = {
    type: EMBED_NAVIGATE_MESSAGE_TYPE,
    target,
    ...(options?.externalContractId && {
      externalContractId: options.externalContractId,
    }),
    ...(options?.cardPaymentRequestId && {
      cardPaymentRequestId: options.cardPaymentRequestId,
    }),
  };
  window.parent?.postMessage(message, "*");
  return true;
}

interface EmbedOpenUrlMessage {
  type: typeof EMBED_OPEN_URL_MESSAGE_TYPE;
  url: string;
}

/**
 * 부모에게 외부 URL 을 새 창으로 열어 달라고 요청(카드 결제창).
 *
 * 임베드 밖에서는 부모가 없으므로 `false` 를 돌려준다 — 호출부가 그때는 직접 `window.open`
 * 하도록 갈래를 준다. 최상단 창에서는 사용자 클릭 콜스택이 살아 있어 팝업 차단이 걸리지 않는다.
 */
export function requestParentOpenUrl(url: string): boolean {
  if (typeof window === "undefined" || !isEmbeddedInIframe()) return false;
  const message: EmbedOpenUrlMessage = {
    type: EMBED_OPEN_URL_MESSAGE_TYPE,
    url,
  };
  window.parent?.postMessage(message, "*");
  return true;
}

interface EmbedReadyMessage {
  type: typeof EMBED_READY_MESSAGE_TYPE;
}

/**
 * 문서당 한 번만 보낸다. 임베드는 같은 문서 안에서 화면을 갈아끼우므로
 * (`/embed/chat-list` → `/embed/chat/:roomId`) 두 번째 화면이 준비될 때 READY 가
 * 또 나가는데, 그때 부모 스플래시는 이미 걷힌 뒤라 의미가 없다. 부모가 첫 신호만
 * 처리하도록 맡기지 않고 보내는 쪽에서 막는다 — 규격이 한 문장 짧아진다.
 */
let hasNotifiedReady = false;

/**
 * 부모에게 세모 화면이 다 그려졌음을 통지. 부모는 이걸 받고 스플래시를 걷는다.
 *
 * **언제 부르는지가 이 신호의 전부다.** 컴포넌트가 마운트된 순간이 아니라 화면에서
 * 전면 로딩 자리표시자가 사라진 순간이어야 한다. 임베드 부팅은
 * 백지 → 로고 스피너(lazy 청크) → "준비하는 중…"(OTT 교환) → 스켈레톤(목록·방 조회)
 * 순으로 로딩 화면이 네 겹인데, 이 중 아무 데서나 부르면 스플래시가 걷힌 자리에
 * 그 다음 로딩 화면이 그대로 드러난다. 호출부는 `useNotifyEmbedReady` 를 쓴다.
 *
 * 성공만이 아니라 **에러 확정도 최종 상태**라 거기서도 불러야 한다. 안 그러면 부모가
 * 타임아웃(8초)까지 스플래시를 붙들고 있다가 에러 화면을 보여준다 — 지금(약 0.8초)보다
 * 한참 나쁘다.
 *
 * `notifyParentEmbedClose` 와 같이 `window.parent` 로만 쏜다. 팝업(top-level)으로 열린
 * 흐름에는 부모 스플래시가 없어 self-post 가 되지만 무해하다.
 */
export function notifyParentEmbedReady(): void {
  if (typeof window === "undefined") return;
  if (hasNotifiedReady) return;
  hasNotifiedReady = true;
  const message: EmbedReadyMessage = { type: EMBED_READY_MESSAGE_TYPE };
  window.parent?.postMessage(message, "*");
}

/**
 * 현재 페이지가 iframe(부모 모달 안) 에서 열렸는지. 팝업(top-level)에서 열린 경우 false.
 */
export function isEmbeddedInIframe(): boolean {
  if (typeof window === "undefined") return false;
  return window.self !== window.top;
}
